import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import { exportToCSV } from '../exportUtils';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import '../Reports/ReportDashboard.css';
import './ReservationList.css';
import '../lib-page-header.css';
import FilterSelect from '../components/FilterSelect';

import {
  getReservationsByLibrary,
  getAvailableCopiesByBookId,
  approveReservationCopy,
  rejectReservation,
  cancelReservation,
  getErrorMessage
} from '../../api/api';

const PAGE_SIZE = 10;

// Single source of truth for reservation statuses: drives the filter dropdown
// and the badge colors.
const STATUS_OPTIONS = [
  { value: 'requested', label: 'Requested', badge: 'bg-warning text-dark' },
  { value: 'approved', label: 'Approved', badge: 'bg-info text-dark' },
  { value: 'ready_for_pickup', label: 'Ready For Pickup', badge: 'bg-primary' },
  { value: 'collected', label: 'Collected', badge: 'bg-success' },
  { value: 'cancelled', label: 'Cancelled', badge: 'bg-secondary' },
  { value: 'rejected', label: 'Rejected', badge: 'bg-danger' },
  { value: 'expired', label: 'Expired', badge: 'bg-dark' }
];

const STATUS_BADGE = STATUS_OPTIONS.reduce((acc, s) => {
  acc[s.value] = s.badge;
  return acc;
}, {});

const ReservationList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const sectionBase = useSectionBase();

  const libraryId = useActiveLibraryId();
  const staffId = user?.id || user?.staff_id;

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  // Single source of truth for which actions a reservation status allows.
  // `approved` = request accepted but no copy assigned yet, so it still needs
  // the assign-copy flow (which moves it on to ready_for_pickup).
  const canApproveReject = (status) => status === 'requested' || status === 'approved';
  const canIssue = (status) => status === 'ready_for_pickup';
  const canCancel = (status) => ['requested', 'approved', 'ready_for_pickup'].includes(status);
  const hasAction = (status) => canApproveReject(status) || canIssue(status) || canCancel(status);

  const normalizeList = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  const getMemberName = (item) => {
    const fullName = `${item.first_name || ''} ${item.last_name || ''}`.trim();

    return (
      item.member_name ||
      item.parent_name ||
      item.user_name ||
      item.full_name ||
      fullName ||
      '-'
    );
  };

  const getStudentName = (item) => {
    return (
      item.student_name ||
      item.child_name ||
      item.student_full_name ||
      '-'
    );
  };

  const getAdmissionNo = (item) => {
    return (
      item.admission_no ||
      item.student_admission_no ||
      '-'
    );
  };

  // Centralized field accessors so the fallback order stays identical across
  // the table, modal, CSV export, and search.
  const getPhone = (item) => item.phone || item.mobile || '-';
  const getBookTitle = (item) => item.book_title || item.title || item.book_name || '-';
  const getAuthor = (item) => item.author_name || item.author || '-';
  const getIsbn = (item) => item.isbn || '-';
  const getPublisher = (item) => item.publisher_name || '-';
  const getCopyAccession = (item) => item.accession_no || item.copy_accession_no || '-';
  const getCopyBarcode = (item) => item.barcode || item.copy_barcode || '-';
  const getClassLabel = (item) => {
    const cls = item.class_name || item.class || '-';
    return item.section ? `${cls} - ${item.section}` : cls;
  };

  const formatDate = (date) => {
    if (!date) return '-';

    try {
      return new Date(date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return String(date).slice(0, 10);
    }
  };

  const formatStatus = (status) => {
    if (!status) return '-';

    return String(status)
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getStatusBadge = (status) => STATUS_BADGE[status] || 'bg-light text-dark';

  const fetchReservations = useCallback(async () => {
    try {
      if (!libraryId) return;

      setLoading(true);

      // Load the whole library set once; status is filtered client-side so
      // changing the dropdown is instant and never triggers a refetch.
      const response = await getReservationsByLibrary(libraryId);

      setReservations(normalizeList(response));
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [libraryId]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const handleApprove = async (reservation) => {
    if (processingId) return;

    try {
      if (!reservation.book_id) {
        Swal.fire('Error', 'Book ID not found in reservation', 'error');
        return;
      }

      const response = await getAvailableCopiesByBookId(reservation.book_id);
      const copies = normalizeList(response);

      if (!copies || copies.length === 0) {
        Swal.fire('No Copy Available', 'No available copy found for this book.', 'warning');
        return;
      }

      const inputOptions = {};

      copies.forEach((copy) => {
        inputOptions[copy.id] = `${copy.accession_no || '-'} | ${copy.barcode || '-'} | Copy ${copy.copy_number || '-'}`;
      });

      const result = await Swal.fire({
        title: 'Assign Book Copy',
        iconHtml: '<i class="fas fa-book-reader"></i>',
        input: 'select',
        inputOptions,
        inputPlaceholder: 'Select available copy',
        showCancelButton: true,
        confirmButtonText: '<i class="fas fa-check me-1"></i> Approve & Assign',
        cancelButtonText: 'Cancel',
        buttonsStyling: false,
        customClass: {
          popup: 'lib-swal',
          icon: 'lib-swal__icon',
          title: 'lib-swal__title',
          htmlContainer: 'lib-swal__body',
          input: 'lib-swal__select',
          actions: 'lib-swal__actions',
          confirmButton: 'lib-swal__confirm',
          cancelButton: 'lib-swal__cancel'
        },
        inputValidator: (value) => {
          if (!value) return 'Please select a copy';
          return null;
        }
      });

      if (!result.isConfirmed) return;

      const payload = {
        reserved_copy_id: result.value,
        approved_by_staff_id: staffId,
        status: 'ready_for_pickup',
        expires_at: null,
        remarks: 'Approved by librarian'
      };

      setProcessingId(reservation.id);
      await approveReservationCopy(reservation.id, payload);
      await fetchReservations();

      Swal.fire('Success', 'Reservation approved and copy assigned.', 'success');
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (reservation) => {
    if (processingId) return;

    const result = await Swal.fire({
      title: 'Reject request?',
      input: 'textarea',
      inputLabel: 'Reason',
      inputPlaceholder: 'Enter rejection reason',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'Please enter rejection reason';
        return null;
      }
    });

    if (!result.isConfirmed) return;

    try {
      const payload = {
        staff_id: staffId,
        rejected_by_staff_id: staffId,
        remarks: result.value || 'Rejected by librarian'
      };

      setProcessingId(reservation.id);
      await rejectReservation(reservation.id, payload);
      await fetchReservations();

      Swal.fire('Rejected', 'Reservation rejected successfully.', 'success');
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancel = async (reservation) => {
    if (processingId) return;

    const result = await Swal.fire({
      title: 'Cancel Reservation?',
      text: `Cancel the request for "${getBookTitle(reservation)}" by ${getMemberName(reservation)}?`,
      input: 'textarea',
      inputLabel: 'Reason (optional)',
      inputPlaceholder: 'Enter cancellation reason...',
      showCancelButton: true,
      confirmButtonText: 'Yes, Cancel It',
      cancelButtonText: 'No',
      icon: 'warning',
      confirmButtonColor: '#d97706'
    });

    if (!result.isConfirmed) return;

    try {
      const payload = {
        cancelled_by_staff_id: staffId,
        remarks: result.value?.trim() || 'Cancelled by librarian'
      };

      setProcessingId(reservation.id);
      await cancelReservation(reservation.id, payload);
      await fetchReservations();

      Swal.fire('Cancelled', 'Reservation cancelled successfully.', 'success');
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const filteredReservations = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    // Parse with an explicit local time so day boundaries aren't shifted by the
    // UTC-midnight interpretation of a bare "yyyy-mm-dd" string.
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const to = toDate ? new Date(`${toDate}T23:59:59.999`) : null;

    return reservations.filter((item) => {
      if (statusFilter && item.status !== statusFilter) return false;

      if (keyword) {
        const matchesKeyword =
          String(getMemberName(item)).toLowerCase().includes(keyword) ||
          String(getPhone(item)).toLowerCase().includes(keyword) ||
          String(item.email || '').toLowerCase().includes(keyword) ||
          String(item.membership_no || '').toLowerCase().includes(keyword) ||
          String(item.member_source || '').toLowerCase().includes(keyword) ||
          String(getBookTitle(item)).toLowerCase().includes(keyword) ||
          String(getAuthor(item)).toLowerCase().includes(keyword) ||
          String(getIsbn(item)).toLowerCase().includes(keyword) ||
          String(getPublisher(item)).toLowerCase().includes(keyword) ||
          String(getStudentName(item)).toLowerCase().includes(keyword) ||
          String(getAdmissionNo(item)).toLowerCase().includes(keyword) ||
          String(item.status || '').toLowerCase().includes(keyword);

        if (!matchesKeyword) return false;
      }

      if (from || to) {
        const requestedOn = item.requested_at || item.created_at;
        const requestedDate = requestedOn ? new Date(requestedOn) : null;

        if (!requestedDate) return false;
        if (from && requestedDate < from) return false;
        if (to && requestedDate > to) return false;
      }

      return true;
    });
  }, [reservations, search, statusFilter, fromDate, toDate]);

  const sortedReservations = useMemo(() => {
    if (!sortField) return filteredReservations;

    const list = [...filteredReservations];

    list.sort((a, b) => {
      let valA;
      let valB;

      if (sortField === 'requested_at') {
        valA = new Date(a.requested_at || a.created_at || 0).getTime();
        valB = new Date(b.requested_at || b.created_at || 0).getTime();
      } else {
        valA = String(a.status || '').toLowerCase();
        valB = String(b.status || '').toLowerCase();
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [filteredReservations, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedReservations.length / PAGE_SIZE));

  const paginatedReservations = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedReservations.slice(start, start + PAGE_SIZE);
  }, [sortedReservations, page]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, fromDate, toDate]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Close the detail modal on Escape.
  useEffect(() => {
    if (!selectedReservation) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedReservation(null);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedReservation]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return <i className="fas fa-sort text-muted ms-1" />;
    return sortDir === 'asc' ? (
      <i className="fas fa-sort-up ms-1" />
    ) : (
      <i className="fas fa-sort-down ms-1" />
    );
  };

  const exportColumns = [
    { header: 'Member/Parent', key: '__member' },
    { header: 'Book', key: '__book' },
    { header: 'Student', key: '__student' },
    { header: 'Requested On', key: '__date' },
    { header: 'Assigned Copy', key: '__copy' },
    { header: 'Status', key: '__status' }
  ];

  const handleExportCSV = () => {
    const rows = sortedReservations.map((item) => ({
      __member: getMemberName(item),
      __book: getBookTitle(item),
      __student: getStudentName(item),
      __date: formatDate(item.requested_at || item.created_at),
      __copy: getCopyAccession(item),
      __status: formatStatus(item.status)
    }));

    exportToCSV(exportColumns, rows, 'parent_book_requests');
  };

  const handleReadyForIssue = (item) => {
    // Pass the full reservation so the issue screen can populate itself even if
    // the reservation isn't resolvable from its own ready_for_pickup list.
    navigate(`${sectionBase}/issues`, {
      state: { reservationId: item.id, reservation: item }
    });
  };

  // Shared action renderer used by both the table row and the detail modal so
  // the status rules can't drift between the two. `beforeAction` lets the modal
  // close itself before firing the handler; `variant` switches button styling.
  const renderActions = (item, { variant = 'row', beforeAction } = {}) => {
    const isBusy = processingId === item.id;
    const run = (handler) => () => {
      if (beforeAction) beforeAction();
      handler(item);
    };

    if (variant === 'modal') {
      return (
        <>
          {canApproveReject(item.status) && (
            <>
              <button type="button" className="btn btn-success" disabled={isBusy} onClick={run(handleApprove)}>
                {isBusy ? 'Working...' : item.status === 'approved' ? 'Assign Copy' : 'Approve'}
              </button>
              <button type="button" className="btn btn-danger" disabled={isBusy} onClick={run(handleReject)}>
                Reject
              </button>
            </>
          )}
          {canIssue(item.status) && (
            <button type="button" className="btn btn-primary" disabled={isBusy} onClick={run(handleReadyForIssue)}>
              Ready for Issue
            </button>
          )}
          {canCancel(item.status) && (
            <button type="button" className="btn btn-warning" disabled={isBusy} onClick={run(handleCancel)}>
              <i className="fas fa-ban me-1" />{isBusy ? 'Working...' : 'Cancel'}
            </button>
          )}
        </>
      );
    }

    // Table row variant — compact styled buttons
    // Cancel shown in row only for ready_for_pickup (Approve+Reject rows are already 2 buttons)
    return (
      <>
        {canApproveReject(item.status) && (
          <>
            <button type="button" className="res-btn res-btn--approve" disabled={isBusy} onClick={run(handleApprove)}>
              {isBusy ? '...' : item.status === 'approved' ? 'Assign' : 'Approve'}
            </button>
            <button type="button" className="res-btn res-btn--reject" disabled={isBusy} onClick={run(handleReject)}>
              Reject
            </button>
          </>
        )}
        {canIssue(item.status) && (
          <>
            <button type="button" className="res-btn res-btn--issue" disabled={isBusy} onClick={run(handleReadyForIssue)}>
              <i className="fas fa-book-open me-1" />Issue
            </button>
            <button type="button" className="res-btn res-btn--cancel" disabled={isBusy} onClick={run(handleCancel)}>
              <i className="fas fa-ban me-1" />Cancel
            </button>
          </>
        )}
      </>
    );
  };

  const stats = useMemo(() => {
    return {
      total: reservations.length,
      requested: reservations.filter((item) => item.status === 'requested').length,
      ready: reservations.filter((item) => item.status === 'ready_for_pickup').length,
      collected: reservations.filter((item) => item.status === 'collected').length,
      rejected: reservations.filter((item) => item.status === 'rejected').length
    };
  }, [reservations]);

  return (
    <div className="reservation-page container-fluid mt-3">
      <div className="lib-page-header ms-0">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-calendar-check" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Parent Book Requests</h3>
            <div className="lib-page-header__subtitle">Approve parent requests and assign available book copy</div>
          </div>
        </div>
      </div>

      <div className="report-grid reservation-stats-grid">
        <div className="report-card" style={{ '--accent': '#6366f1' }}>
          <span className="report-card__icon" style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}><i className="fas fa-layer-group" /></span>
          <span className="report-card__title">Total</span>
          <span className="report-card__value">{stats.total}</span>
        </div>

        <div className="report-card" style={{ '--accent': '#f59e0b' }}>
          <span className="report-card__icon" style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}><i className="fas fa-hourglass-half" /></span>
          <span className="report-card__title">Requested</span>
          <span className="report-card__value">{stats.requested}</span>
        </div>

        <div className="report-card" style={{ '--accent': '#0ea5e9' }}>
          <span className="report-card__icon" style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}><i className="fas fa-box-open" /></span>
          <span className="report-card__title">Ready</span>
          <span className="report-card__value">{stats.ready}</span>
        </div>

        <div className="report-card" style={{ '--accent': '#22b07d' }}>
          <span className="report-card__icon" style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}><i className="fas fa-check-circle" /></span>
          <span className="report-card__title">Collected</span>
          <span className="report-card__value">{stats.collected}</span>
        </div>

        <div className="report-card" style={{ '--accent': '#ef5350' }}>
          <span className="report-card__icon" style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}><i className="fas fa-times-circle" /></span>
          <span className="report-card__title">Rejected</span>
          <span className="report-card__value">{stats.rejected}</span>
        </div>
      </div>

      <div className="reservation-main-card">
        <div className="reservation-filter-row">
          <div className="reservation-filter-item">
            <input
              type="text"
              className="form-control"
              placeholder="Search parent, phone, membership, book, ISBN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="reservation-filter-item">
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[{ value: '', label: 'All Status' }, ...STATUS_OPTIONS]}
            />
          </div>

          <div className="reservation-filter-item reservation-filter-item--date">
            <span className="res-date-label">From</span>
            <input
              type="date"
              className="form-control"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="reservation-filter-item reservation-filter-item--date">
            <span className="res-date-label">To</span>
            <input
              type="date"
              className="form-control"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <div className="reservation-filter-actions">
            <button
              type="button"
              className="res-filter-btn res-filter-btn--reset"
              onClick={() => { setSearch(''); setStatusFilter(''); setFromDate(''); setToDate(''); }}
            >
              <i className="fas fa-undo me-1" />Reset
            </button>
            <button type="button" className="res-filter-btn res-filter-btn--csv" onClick={handleExportCSV}>
              <i className="fas fa-file-csv me-1" />CSV
            </button>
          </div>

          <div className="reservation-showing-text">
            Showing {sortedReservations.length} of {reservations.length}
          </div>
        </div>

        {showLoader ? (
          <Loader label="Loading reservations..." />
        ) : (
          <div className="reservation-table-wrapper">
            <table className="table table-bordered table-hover align-middle reservation-table">
              <thead>
                <tr>
                  <th className="col-sno">#</th>
                  <th className="col-member">Member / Parent</th>
                  <th className="col-book">Book</th>
                  <th className="col-student">Student</th>
                  <th
                    className="col-date"
                    role="button"
                    onClick={() => handleSort('requested_at')}
                  >
                    Requested On {sortIcon('requested_at')}
                  </th>
                  <th className="col-copy">Assigned Copy</th>
                  <th
                    className="col-status"
                    role="button"
                    onClick={() => handleSort('status')}
                  >
                    Status {sortIcon('status')}
                  </th>
                  <th className="col-action">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedReservations.length > 0 ? (
                  paginatedReservations.map((item, index) => (
                    <tr
                      key={item.id}
                      role="button"
                      title="Click row to view full details"
                      onClick={() => setSelectedReservation(item)}
                    >
                      <td>{(page - 1) * PAGE_SIZE + index + 1}</td>

                      <td>
                        <strong>{getMemberName(item)}</strong>
                        <div className="small text-muted">
                          {getPhone(item)}
                        </div>
                        <div className="small text-muted">
                          {item.membership_no || '-'}
                        </div>
                      </td>

                      <td>
                        <strong>{getBookTitle(item)}</strong>
                        <div className="small text-muted">
                          {getAuthor(item)}
                        </div>
                        <div className="small text-muted">
                          {getIsbn(item)}
                        </div>
                      </td>

                      <td>
                        <strong>{getStudentName(item)}</strong>
                        <div className="small text-muted">
                          Adm: {getAdmissionNo(item)}
                        </div>
                        <div className="small text-muted">
                          Class: {getClassLabel(item)}
                        </div>
                      </td>

                      <td>
                        {formatDate(item.requested_at || item.created_at)}
                      </td>

                      <td>
                        <strong>{getCopyAccession(item)}</strong>
                        <div className="small text-muted">
                          {getCopyBarcode(item)}
                        </div>
                      </td>

                      <td>
                        <span className={`res-status-badge res-status--${item.status || 'unknown'}`}>
                          {formatStatus(item.status)}
                        </span>
                      </td>

                      <td onClick={(e) => e.stopPropagation()}>
                        {hasAction(item.status) && (
                          <div className="res-action-cell">
                            <button
                              type="button"
                              className="res-btn res-btn--view"
                              onClick={() => setSelectedReservation(item)}
                            >
                              <i className="fas fa-eye me-1" />View
                            </button>
                            {renderActions(item, { variant: 'row' })}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-5">
                      No reservations found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {sortedReservations.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                <div className="small text-muted">
                  Page {page} of {totalPages}
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  >
                    Previous
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page >= totalPages}
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedReservation && (
        <div
          className="reservation-modal-backdrop"
          onClick={() => setSelectedReservation(null)}
        >
          <div
            className="reservation-modal-box"
            role="dialog"
            aria-modal="true"
            aria-label="Reservation Details"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="reservation-modal-header">
              <h5>Reservation Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setSelectedReservation(null)}
              />
            </div>

            <div className="reservation-modal-body">
              <div className="reservation-detail-grid">
                <div className="reservation-detail-card rc-member">
                  <h6><i className="fas fa-user-tie" /> Member / Parent Details</h6>

                  <p>
                    <strong>Name:</strong> {getMemberName(selectedReservation)}
                  </p>

                  <p>
                    <strong>Phone:</strong> {getPhone(selectedReservation)}
                  </p>

                  <p>
                    <strong>Email:</strong> {selectedReservation.email || '-'}
                  </p>

                  <p>
                    <strong>Membership No:</strong> {selectedReservation.membership_no || '-'}
                  </p>

                  <p>
                    <strong>Source:</strong> {selectedReservation.member_source || '-'}
                  </p>
                </div>

                <div className="reservation-detail-card rc-student">
                  <h6><i className="fas fa-user-graduate" /> Student Details</h6>

                  <p>
                    <strong>Name:</strong> {getStudentName(selectedReservation)}
                  </p>

                  <p>
                    <strong>Admission No:</strong> {getAdmissionNo(selectedReservation)}
                  </p>

                  <p>
                    <strong>Class:</strong> {getClassLabel(selectedReservation)}
                  </p>
                </div>

                <div className="reservation-detail-card rc-book">
                  <h6><i className="fas fa-book" /> Book Details</h6>

                  <p>
                    <strong>Title:</strong> {getBookTitle(selectedReservation)}
                  </p>

                  <p>
                    <strong>Author:</strong> {getAuthor(selectedReservation)}
                  </p>

                  <p>
                    <strong>ISBN:</strong> {getIsbn(selectedReservation)}
                  </p>

                  <p>
                    <strong>Publisher:</strong> {getPublisher(selectedReservation)}
                  </p>
                </div>

                <div className="reservation-detail-card rc-copy">
                  <h6><i className="fas fa-copy" /> Copy Details</h6>

                  <p>
                    <strong>Reserved Copy ID:</strong> {selectedReservation.reserved_copy_id || '-'}
                  </p>

                  <p>
                    <strong>Accession No:</strong> {getCopyAccession(selectedReservation)}
                  </p>

                  <p>
                    <strong>Barcode:</strong> {getCopyBarcode(selectedReservation)}
                  </p>

                  <p>
                    <strong>Copy Status:</strong> {selectedReservation.copy_availability_status || '-'}
                  </p>
                </div>

                <div className="reservation-detail-card reservation-detail-full rc-status">
                  <h6><i className="fas fa-clipboard-check" /> Request Status</h6>

                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={`badge ${getStatusBadge(selectedReservation.status)}`}>
                      {formatStatus(selectedReservation.status)}
                    </span>
                  </p>

                  <p>
                    <strong>Requested On:</strong> {formatDate(selectedReservation.requested_at || selectedReservation.created_at)}
                  </p>

                  <p>
                    <strong>Expires At:</strong> {formatDate(selectedReservation.expires_at)}
                  </p>

                  <p>
                    <strong>Remarks:</strong> {selectedReservation.remarks || '-'}
                  </p>

                  <p>
                    <strong>Approved By:</strong>{' '}
                    {`${selectedReservation.approved_by_first_name || ''} ${selectedReservation.approved_by_last_name || ''}`.trim() || '-'}
                  </p>
                </div>
              </div>
            </div>

            <div className="reservation-modal-footer">
              {renderActions(selectedReservation, {
                variant: 'modal',
                beforeAction: () => setSelectedReservation(null)
              })}

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSelectedReservation(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationList;